import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StyledMobileDatePicker, dateTimePickerSlotProps, getCommonPickerStyles } from '../../themes/MUIStyles';
import { format } from 'date-fns';
import { useRecurringTasks } from '../../hooks/useRecurringTasks';
import { useDispatch, useSelector } from 'react-redux';
import { addRecurringTask } from '../../store/recurringTasksSlice';
import { updateSetting } from '../../store/settingsSlice';

function DaySelector({ type, value, onChange }) {
  const days = type === 'week' 
    ? [
        { value: 0, label: 'Sun' },
        { value: 1, label: 'Mon' },
        { value: 2, label: 'Tue' },
        { value: 3, label: 'Wed' },
        { value: 4, label: 'Thu' },
        { value: 5, label: 'Fri' },
        { value: 6, label: 'Sat' }
      ]
    : Array.from({ length: 31 }, (_, i) => ({
        value: i + 1,
        label: String(i + 1)
      }));

  return (
    <div className={`grid ${type === 'week' 
      ? 'grid-cols-4 sm:grid-cols-7' 
      : 'grid-cols-4 sm:grid-cols-7 md:grid-cols-8'} gap-1 w-full max-w-[400px]`}>
      {days.map(day => (
        <button
          key={day.value}
          type="button"
          onClick={() => onChange(day.value)}
          className={`p-1 sm:p-2 rounded-md text-xs sm:text-sm transition-colors
            ${Number(value) === day.value 
              ? 'bg-[var(--primary-button-active)] text-[var(--text-primary)]' 
              : 'bg-[var(--surface-bg)] text-[var(--text-disabled)] hover:bg-[var(--surface-bg-lighter)] hover:text-[var(--text-primary)]'
            }`}
        >
          {day.label}
        </button>
      ))}
    </div>
  );
}

function YearlyDateSelector({ monthValue, dayValue, onMonthChange, onDayChange }) {
  const months = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' }
  ];

  const selectedMonth = monthValue ? Number(monthValue) : null;
  const daysInMonth = selectedMonth ? new Date(2024, selectedMonth, 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => ({
    value: i + 1,
    label: String(i + 1)
  }));

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium mb-2 text-[var(--text-muted)]">Select Month:</h4>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1 w-full max-w-[400px]">
          {months.map(month => (
            <button
              key={month.value}
              type="button"
              onClick={() => onMonthChange(month.value)}
              className={`p-2 rounded-md text-xs sm:text-sm transition-colors
                ${Number(monthValue) === month.value 
                  ? 'bg-[var(--primary-button-active)] text-[var(--text-primary)]' 
                  : 'bg-[var(--surface-bg)] text-[var(--text-disabled)] hover:bg-[var(--surface-bg-lighter)] hover:text-[var(--text-primary)]'
                }`}
            >
              {month.label}
            </button>
          ))}
        </div>
      </div>
      {selectedMonth && (
        <div>
          <h4 className="text-sm font-medium mb-2 text-[var(--text-muted)]">Select Day:</h4>
          <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-8 gap-1 w-full max-w-[400px]">
            {days.map(day => (
              <button
                key={day.value}
                type="button"
                onClick={() => onDayChange(day.value)}
                className={`p-2 rounded-md text-xs sm:text-sm transition-colors
                  ${Number(dayValue) === day.value 
                    ? 'bg-[var(--primary-button-active)] text-[var(--text-primary)]' 
                    : 'bg-[var(--surface-bg)] text-[var(--text-disabled)] hover:bg-[var(--surface-bg-lighter)] hover:text-[var(--text-primary)]'
                  }`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskHistory({ task, onMarkDone, onLastCompletedChange }) {
  const { calculateTaskDates } = useRecurringTasks();
  const dates = useMemo(() => calculateTaskDates(task), [task, calculateTaskDates]);

  const handleLastDoneChange = (newDate) => {
    if (newDate) {
      // Normalize the date to midnight
      const normalizedDate = new Date(newDate);
      normalizedDate.setHours(0, 0, 0, 0);
      
      // Call the parent handler with the normalized date
      onLastCompletedChange(normalizedDate);
    }
  };

  if (!dates) return null;

  return (
    <div className="bg-[var(--surface-bg-darker)] p-6 rounded-lg space-y-4 mt-8 text-left">
      <h3 className="text-lg font-medium text-[var(--text-secondary)]">Task History</h3>
      <div className="space-y-2">
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-[var(--text-muted)] w-[100px] shrink-0">Last done:</span>
          <div className="flex items-center gap-2 min-w-[200px]">
            <div className="shrink-0">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledMobileDatePicker
                  value={dates.lastCompleted ? new Date(dates.lastCompleted) : null}
                  onChange={handleLastDoneChange}
                  slotProps={{
                    ...dateTimePickerSlotProps,
                    textField: getCommonPickerStyles('auto')
                  }}
                />
              </LocalizationProvider>
            </div>
            <button
              onClick={() => onMarkDone(new Date())}
              className="base-button flash-button rounded-md !py-1 !px-2 !text-sm shrink-0"
            >
              Mark Done Today
            </button>
          </div>
        </div>

        {task.scheduleMode !== 'fixed' && dates.gracePeriodRange && dates.preparationRange && (
          <>
            <div className="flex items-center gap-4">
              <span className="text-[var(--text-muted)] w-[100px]">Grace Period:</span>
              <span className="text-[var(--text-primary)]">
                {format(new Date(dates.gracePeriodRange.start), 'MM/dd/yyyy')} to{' '}
                {format(new Date(dates.gracePeriodRange.end), 'MM/dd/yyyy')}
              </span>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-[var(--text-muted)] w-[100px]">Preparation:</span>
              <span className="text-[var(--text-primary)]">
                {format(new Date(dates.preparationRange.start), 'MM/dd/yyyy')} to{' '}
                {format(new Date(dates.preparationRange.end), 'MM/dd/yyyy')}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RecurringTasks() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useSelector(state => state.settings.settings);
  const { 
    tasks: recurringTasks,
    handleUpdateTask: updateRecurringTask,
    handleCompleteTask,
    handleSetActiveTask
  } = useRecurringTasks();

  const activeRecurringTask = taskId ? recurringTasks.find(t => t.id === taskId) : null;

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    scheduleMode: 'fixed',
    scheduleType: 'week',
    dayOfWeek: null,
    dateOfMonth: null,
    monthOfYear: null,
    dayOfYear: null,
    frequency: 'Weekly',
    preparationTime: 0,
    gracePeriod: 0,
    nextDueDate: new Date(),
  });

  // Add update timer refs
  const updateTimerRef = useRef(null);
  const lastUpdateRef = useRef({});

  // Effect to handle numeric field updates
  useEffect(() => {
    if (taskId && taskId !== 'new' && activeRecurringTask) {
      const numericFields = ['preparationTime', 'gracePeriod'];
      const updates = {};
      let hasUpdates = false;

      const MAX_DAYS = 9999; // Maximum number of days allowed

      numericFields.forEach(field => {
        const currentValue = formData[field];
        const lastValue = lastUpdateRef.current[field];
        
        if (currentValue !== lastValue && 
            (currentValue === '' || /^\d+$/.test(currentValue))) {
          // Parse and clamp the value
          const parsedValue = currentValue === '' ? 0 : parseInt(currentValue, 10);
          const clampedValue = Math.min(Math.max(parsedValue, 0), MAX_DAYS);
          
          // Only update if the value is different from the last update
          if (clampedValue !== lastUpdateRef.current[field]) {
            updates[field] = clampedValue;
            lastUpdateRef.current[field] = currentValue;
            hasUpdates = true;
          }
        }
      });

      if (hasUpdates) {
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
        }

        updateTimerRef.current = setTimeout(() => {
          updateRecurringTask(taskId, { ...updates, forceRecalculate: true });
        }, 500);
      }
    }

    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, [taskId, formData.preparationTime, formData.gracePeriod, updateRecurringTask, activeRecurringTask, formData]);

  useEffect(() => {
    if (!taskId && recurringTasks.length > 0) {
      const lastOpenedTaskId = settings.lastOpenedRecurringTaskId;
      const targetTask = lastOpenedTaskId && recurringTasks.find(t => t.id === lastOpenedTaskId)
        ? lastOpenedTaskId 
        : recurringTasks[0].id;
      navigate(`/recurring-tasks/${targetTask}`);
    } else if (taskId && recurringTasks.length === 0 && taskId !== 'new') {
      navigate('/recurring-tasks');
    } else if (taskId && taskId !== 'new') {
      const task = recurringTasks.find(t => t.id === taskId);
      if (task) {
        handleSetActiveTask(task);
        
        if (!formData.title || task.title !== formData.title) {
          setFormData(prevData => ({
            ...prevData,
            title: task.title,
            scheduleMode: task.scheduleMode || 'fixed',
            scheduleType: task.scheduleType || 'week',
            dayOfWeek: task.dayOfWeek || null,
            dateOfMonth: task.dateOfMonth || null,
            monthOfYear: task.monthOfYear || null,
            dayOfYear: task.dayOfYear || null,
            frequency: task.frequency,
            preparationTime: task.preparationTime,
            gracePeriod: task.gracePeriod,
            nextDueDate: task.nextDueDate ? new Date(task.nextDueDate) : new Date(),
          }));
        }
      }
    }
  }, [taskId, recurringTasks, navigate, formData.title, handleSetActiveTask, settings.lastOpenedRecurringTaskId]);

  useEffect(() => {
    if (taskId === 'new') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setFormData({
        title: '',
        scheduleMode: 'fixed',
        scheduleType: 'week',
        dayOfWeek: null,
        dateOfMonth: null,
        monthOfYear: null,
        dayOfYear: null,
        frequency: 'Weekly',
        preparationTime: 0,
        gracePeriod: 0,
        nextDueDate: today,
      });
      setIsEditing(false);
      setEditValue('');
    } else if (activeRecurringTask) {
      const nextDueDate = activeRecurringTask.nextDueDate ? new Date(activeRecurringTask.nextDueDate) : new Date();
      nextDueDate.setHours(0, 0, 0, 0);
      
      setFormData({
        title: activeRecurringTask.title,
        scheduleMode: activeRecurringTask.scheduleMode || 'fixed',
        scheduleType: activeRecurringTask.scheduleType || 'week',
        dayOfWeek: activeRecurringTask.dayOfWeek || null,
        dateOfMonth: activeRecurringTask.dateOfMonth || null,
        monthOfYear: activeRecurringTask.monthOfYear || null,
        dayOfYear: activeRecurringTask.dayOfYear || null,
        frequency: activeRecurringTask.frequency,
        preparationTime: activeRecurringTask.preparationTime,
        gracePeriod: activeRecurringTask.gracePeriod,
        nextDueDate: nextDueDate,
      });
      setEditValue(activeRecurringTask.title);
    }
  }, [activeRecurringTask, taskId]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(formData.title);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSubmitEdit = () => {
    if (editValue.trim() && editValue !== formData.title) {
      setFormData(prev => ({ ...prev, title: editValue.trim() }));
      if (taskId && taskId !== 'new') {
        updateRecurringTask(taskId, { 
          title: editValue.trim(),
          forceRecalculate: false
        });
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmitEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const getNextOccurrence = (scheduleType, value, monthValue = null) => {
    const today = new Date();
    let nextDate = new Date(today);
    nextDate.setHours(0, 0, 0, 0);
    
    if (scheduleType === 'week' && value !== null) {
      const currentDay = today.getDay();
      const targetDay = Number(value);
      let daysUntilNext = targetDay - currentDay;
      if (daysUntilNext < 0) daysUntilNext += 7;
      // If selecting same day, go to next week
      if (daysUntilNext === 0) daysUntilNext = 7;
      nextDate.setDate(today.getDate() + daysUntilNext);
    } else if (scheduleType === 'month' && value !== null) {
      const targetDate = Number(value);
      nextDate.setDate(targetDate);
      if (nextDate < today) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
      
      // Handle months with fewer days
      const lastDayOfMonth = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
      if (targetDate > lastDayOfMonth) {
        nextDate.setDate(lastDayOfMonth);
      }
    } else if (scheduleType === 'year' && monthValue !== null && value !== null) {
      const targetMonth = Number(monthValue) - 1; // 0-indexed
      const targetDay = Number(value);
      
      nextDate.setMonth(targetMonth);
      nextDate.setDate(targetDay);
      
      // If the date has passed this year, move to next year
      if (nextDate <= today) {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
      
      // Handle Feb 29th for non-leap years
      if (targetMonth === 1 && targetDay === 29) {
        const year = nextDate.getFullYear();
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        if (!isLeapYear) {
          nextDate.setDate(28);
        }
      }
    }
    
    return nextDate;
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    
    // Normalize the date to midnight
    const normalizedDate = new Date(newDate);
    normalizedDate.setHours(0, 0, 0, 0);
    
    if (taskId && taskId !== 'new') {
      const updateData = {
        nextDueDate: normalizedDate.toISOString(),
        forceRecalculate: true,
        scheduleMode: formData.scheduleMode,
        scheduleType: formData.scheduleType,
        dayOfWeek: formData.dayOfWeek,
        dateOfMonth: formData.dateOfMonth,
        monthOfYear: formData.monthOfYear,
        dayOfYear: formData.dayOfYear,
        frequency: formData.frequency,
        preparationTime: formData.preparationTime,
        gracePeriod: formData.gracePeriod,
        isDateUpdate: true
      };

      updateRecurringTask(taskId, updateData)
        .then(response => {
          // Update form data with the response
          setFormData(prevData => ({
            ...prevData,
            nextDueDate: new Date(response.nextDueDate)
          }));
          // Update the active task
          handleSetActiveTask(response);
        })
        .catch(error => {
          console.error('Failed to update date:', error);
          // Revert form data if update fails
          setFormData(prevData => ({
            ...prevData,
            nextDueDate: prevData.nextDueDate
          }));
        });
    } else {
      // For new tasks, just update the form data
      setFormData(prevData => ({
        ...prevData,
        nextDueDate: normalizedDate
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    const newFormData = { ...formData };
    let nextDueDate = null;
    
    if (name === 'dayOfWeek' && value !== null) {
      newFormData.dayOfWeek = Number(value);
      newFormData.dateOfMonth = null;
      newFormData.monthOfYear = null;
      newFormData.dayOfYear = null;
      newFormData.scheduleType = 'week';
      nextDueDate = getNextOccurrence('week', value);
      
      if (taskId && taskId !== 'new') {
        updateRecurringTask(taskId, {
          dayOfWeek: Number(value),
          dateOfMonth: null,
          monthOfYear: null,
          dayOfYear: null,
          scheduleType: 'week',
          nextDueDate: nextDueDate.toISOString(),
          forceRecalculate: true
        });
      }
    } else if (name === 'dateOfMonth' && value !== null) {
      newFormData.dateOfMonth = Number(value);
      newFormData.dayOfWeek = null;
      newFormData.monthOfYear = null;
      newFormData.dayOfYear = null;
      newFormData.scheduleType = 'month';
      nextDueDate = getNextOccurrence('month', value);
      
      if (taskId && taskId !== 'new') {
        updateRecurringTask(taskId, {
          dateOfMonth: Number(value),
          dayOfWeek: null,
          monthOfYear: null,
          dayOfYear: null,
          scheduleType: 'month',
          nextDueDate: nextDueDate.toISOString(),
          forceRecalculate: true
        });
      }
    } else if (name === 'monthOfYear' && value !== null) {
      newFormData.monthOfYear = Number(value);
      newFormData.dayOfWeek = null;
      newFormData.dateOfMonth = null;
      newFormData.scheduleType = 'year';
      
      // Only calculate next occurrence if both month and day are selected
      if (newFormData.dayOfYear !== null) {
        nextDueDate = getNextOccurrence('year', newFormData.dayOfYear, value);
        
        if (taskId && taskId !== 'new') {
          updateRecurringTask(taskId, {
            monthOfYear: Number(value),
            dayOfYear: newFormData.dayOfYear,
            dayOfWeek: null,
            dateOfMonth: null,
            scheduleType: 'year',
            nextDueDate: nextDueDate.toISOString(),
            forceRecalculate: true
          });
        }
      } else {
        // Just update the month without calculating next date yet
        if (taskId && taskId !== 'new') {
          updateRecurringTask(taskId, {
            monthOfYear: Number(value),
            dayOfWeek: null,
            dateOfMonth: null,
            scheduleType: 'year',
            forceRecalculate: false
          });
        }
      }
    } else if (name === 'dayOfYear' && value !== null) {
      newFormData.dayOfYear = Number(value);
      newFormData.dayOfWeek = null;
      newFormData.dateOfMonth = null;
      newFormData.scheduleType = 'year';
      
      // Only calculate next occurrence if both month and day are selected
      if (newFormData.monthOfYear !== null) {
        nextDueDate = getNextOccurrence('year', value, newFormData.monthOfYear);
        
        if (taskId && taskId !== 'new') {
          updateRecurringTask(taskId, {
            dayOfYear: Number(value),
            monthOfYear: newFormData.monthOfYear,
            dayOfWeek: null,
            dateOfMonth: null,
            scheduleType: 'year',
            nextDueDate: nextDueDate.toISOString(),
            forceRecalculate: true
          });
        }
      } else {
        // Just update the day without calculating next date yet
        if (taskId && taskId !== 'new') {
          updateRecurringTask(taskId, {
            dayOfYear: Number(value),
            dayOfWeek: null,
            dateOfMonth: null,
            scheduleType: 'year',
            forceRecalculate: false
          });
        }
      }
    } else if (name === 'preparationTime' || name === 'gracePeriod') {
      // Only allow numbers and empty string
      if (value === '' || /^\d{0,4}$/.test(value)) {
        newFormData[name] = value;
      }
    } else if (name === 'title') {
      // Special handling for title updates
      newFormData.title = value;
      
      if (taskId && taskId !== 'new') {
        const trimmedTitle = value.trim();
        if (trimmedTitle) {
          updateRecurringTask(taskId, {
            title: trimmedTitle
          });
        }
      }
    } else {
      newFormData[name] = value;
      
      if (taskId && taskId !== 'new') {
        const updates = {
          [name]: value,
          forceRecalculate: true
        };
        if (nextDueDate) {
          updates.nextDueDate = nextDueDate.toISOString();
        }
        updateRecurringTask(taskId, updates);
      }
    }

    if (nextDueDate) {
      newFormData.nextDueDate = nextDueDate;
    }
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedTitle = formData.title.trim();
    if (trimmedTitle) {
      const newTask = {
        title: trimmedTitle,
        scheduleMode: formData.scheduleMode,
        scheduleType: formData.scheduleType,
        dayOfWeek: formData.dayOfWeek,
        dateOfMonth: formData.dateOfMonth,
        monthOfYear: formData.monthOfYear,
        dayOfYear: formData.dayOfYear,
        frequency: formData.frequency,
        preparationTime: formData.preparationTime === '' ? 0 : parseInt(formData.preparationTime, 10),
        gracePeriod: formData.gracePeriod === '' ? 0 : parseInt(formData.gracePeriod, 10),
        nextDueDate: formData.nextDueDate,
      };
      
      try {
        const result = await dispatch(addRecurringTask(newTask)).unwrap();
        if (result?.id) {
          navigate(`/recurring-tasks/${result.id}`);
        }
      } catch (error) {
        console.error('Failed to create task:', error);
      }
    }
  };

  const handleScheduleModeChange = (mode) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const newFormData = {
      ...formData,
      scheduleMode: mode,
      scheduleType: mode === 'fixed' ? 'week' : undefined,
      dayOfWeek: null,
      dateOfMonth: null,
      monthOfYear: null,
      dayOfYear: null,
      frequency: mode === 'interval' ? 'Weekly' : undefined,
      nextDueDate: today
    };
    setFormData(newFormData);
    
    if (taskId && taskId !== 'new') {
      const updates = {
        scheduleMode: mode,
        scheduleType: mode === 'fixed' ? 'week' : undefined,
        dayOfWeek: null,
        dateOfMonth: null,
        monthOfYear: null,
        dayOfYear: null,
        frequency: mode === 'interval' ? 'Weekly' : undefined,
        nextDueDate: today.toISOString(),
        forceRecalculate: true
      };
      updateRecurringTask(taskId, updates);
    }
  };

  const handleScheduleTypeChange = (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const newFormData = {
      ...formData,
      scheduleType: type,
      dayOfWeek: null,
      dateOfMonth: null,
      monthOfYear: null,
      dayOfYear: null,
      nextDueDate: today
    };
    setFormData(newFormData);
    
    if (taskId && taskId !== 'new') {
      const updates = {
        scheduleType: type,
        dayOfWeek: null,
        dateOfMonth: null,
        monthOfYear: null,
        dayOfYear: null,
        nextDueDate: today.toISOString(),
        forceRecalculate: true
      };
      updateRecurringTask(taskId, updates);
    }
  };

  const shouldDisableDate = (date) => {
    if (formData.scheduleMode !== 'fixed') return false;
    
    if (formData.scheduleType === 'week') {
      return date.getDay() !== Number(formData.dayOfWeek);
    }
    
    if (formData.scheduleType === 'month' && formData.dateOfMonth !== null) {
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      const targetDate = Number(formData.dateOfMonth);
      
      if (targetDate > lastDayOfMonth) {
        return date.getDate() !== lastDayOfMonth;
      }
      
      return date.getDate() !== targetDate;
    }
    
    if (formData.scheduleType === 'year' && formData.monthOfYear !== null && formData.dayOfYear !== null) {
      const targetMonth = Number(formData.monthOfYear) - 1; // 0-indexed
      const targetDay = Number(formData.dayOfYear);
      
      // Only allow dates that match the selected month and day
      if (date.getMonth() !== targetMonth) return true;
      
      const lastDayOfMonth = new Date(date.getFullYear(), targetMonth + 1, 0).getDate();
      if (targetDay > lastDayOfMonth) {
        return date.getDate() !== lastDayOfMonth;
      }
      
      return date.getDate() !== targetDay;
    }
    
    return false;
  };

  const shouldShowDaySelector = () => {
    if (formData.scheduleMode !== 'fixed') return false;
    if (formData.scheduleType === 'week') return true;
    if (formData.scheduleType === 'month') return true;
    if (formData.scheduleType === 'year') return true;
    return false;
  };

  const handleLastCompletedChange = (date) => {
    if (!taskId || taskId === 'new') return;

    const updateData = {
      lastCompleted: date.toISOString(),
      forceRecalculate: true,
      scheduleMode: formData.scheduleMode,
      scheduleType: formData.scheduleType,
      dayOfWeek: formData.dayOfWeek,
      dateOfMonth: formData.dateOfMonth,
      monthOfYear: formData.monthOfYear,
      dayOfYear: formData.dayOfYear,
      frequency: formData.frequency,
      preparationTime: formData.preparationTime,
      gracePeriod: formData.gracePeriod
    };

    updateRecurringTask(taskId, updateData)
      .then(response => {
        // Update the active task with the response
        handleSetActiveTask(response);
      })
      .catch(error => {
        console.error('Failed to update last completed date:', error);
      });
  };

  // Store last opened task when task changes
  useEffect(() => {
    if (taskId && taskId !== 'new') {
      dispatch(updateSetting({ key: 'lastOpenedRecurringTaskId', value: taskId }));
    }
  }, [taskId, dispatch]);

  if (!taskId) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-muted)]">
        Select a recurring task from the sidebar or create a new one
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="w-full">
        <div className="flex flex-col sm:flex-row justify-start items-start gap-4">
          {taskId === 'new' ? (
            <div className="w-full">
              <h2 className="text-2xl font-semibold text-gray-200 mb-4 text-left">New Recurring Task</h2>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter task title..."
                className="w-full px-3 py-2 text-lg bg-[var(--input-bg)] border border-[var(--input-border)] focus:border-[var(--input-border-hover)] focus:outline-none rounded-md text-[var(--text-primary)] placeholder-[var(--text-muted)]"
              />
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start gap-4 w-full">
              {isEditing ? (
                <>
                  <div className="w-full sm:w-fit">
                    <input
                      ref={inputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSubmitEdit}
                      onKeyDown={handleKeyDown}
                      className="w-full text-2xl font-semibold bg-[var(--surface-bg)] border border-[var(--border-light)] rounded px-2 py-1 focus:outline-none focus:border-[var(--text-primary)]"
                    />
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <button
                      onClick={handleSubmitEdit}
                      className="base-button !px-1.5 !py-1 flex items-center justify-center !rounded-md shrink-0"
                      title="Save task name"
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        check
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full sm:w-fit">
                    <h2 className="text-2xl font-semibold text-[var(--text-primary)] break-words text-left">
                      {formData.title}
                    </h2>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <button
                      onClick={handleStartEdit}
                      className="base-button !px-1.5 !py-1 flex items-center justify-center !rounded-md shrink-0"
                      title="Edit task name"
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        edit
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="space-y-6">
          <div className="text-left">
            <h3 className="text-lg font-medium mb-4">Schedule Type</h3>
            <div className="slider-container">
              <div
                className="slider-track"
                style={{
                  transform: `translateX(${formData.scheduleMode === 'interval' ? '100%' : '0'})`
                }}
              />
              <div className="slider-buttons">
                <button
                  type="button"
                  onClick={() => handleScheduleModeChange('fixed')}
                  className={`slider-button ${formData.scheduleMode === 'fixed' ? 'active' : ''}`}
                >
                  Fixed Schedule
                </button>
                <button
                  type="button"
                  onClick={() => handleScheduleModeChange('interval')}
                  className={`slider-button ${formData.scheduleMode === 'interval' ? 'active' : ''}`}
                >
                  Interval Schedule
                </button>
              </div>
            </div>
          </div>

          {formData.scheduleMode === 'fixed' && (
            <div className="space-y-4">
              <div className="text-left">
                <h3 className="text-lg font-medium mb-4">Schedule Every:</h3>
                <div className="slider-container slider-container-3">
                  <div
                    className="slider-track slider-track-3"
                    style={{
                      transform: `translateX(${
                        formData.scheduleType === 'week' ? '0' :
                        formData.scheduleType === 'month' ? '100%' :
                        '200%'
                      })`
                    }}
                  />
                  <div className="slider-buttons slider-buttons-3">
                    <button
                      type="button"
                      onClick={() => handleScheduleTypeChange('week')}
                      className={`slider-button ${formData.scheduleType === 'week' ? 'active' : ''}`}
                    >
                      <span className="hidden sm:inline">Day of Week</span>
                      <span className="sm:hidden">Week</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScheduleTypeChange('month')}
                      className={`slider-button ${formData.scheduleType === 'month' ? 'active' : ''}`}
                    >
                      <span className="hidden sm:inline">Date of Month</span>
                      <span className="sm:hidden">Month</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScheduleTypeChange('year')}
                      className={`slider-button ${formData.scheduleType === 'year' ? 'active' : ''}`}
                    >
                      <span className="hidden sm:inline">Day of Year</span>
                      <span className="sm:hidden">Year</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-left">
                {formData.scheduleType === 'year' ? (
                  <YearlyDateSelector
                    monthValue={formData.monthOfYear}
                    dayValue={formData.dayOfYear}
                    onMonthChange={(value) => handleChange({
                      target: { name: 'monthOfYear', value }
                    })}
                    onDayChange={(value) => handleChange({
                      target: { name: 'dayOfYear', value }
                    })}
                  />
                ) : shouldShowDaySelector() && (
                  <DaySelector
                    type={formData.scheduleType}
                    value={formData.scheduleType === 'week' ? formData.dayOfWeek : formData.dateOfMonth}
                    onChange={(value) => handleChange({
                      target: {
                        name: formData.scheduleType === 'week' ? 'dayOfWeek' : 'dateOfMonth',
                        value
                      }
                    })}
                  />
                )}
              </div>
            </div>
          )}

          {formData.scheduleMode === 'interval' && (
            <div className="space-y-4 max-w-[600px] text-left">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Frequency</label>
                  <select
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleChange}
                    className="w-[300px] p-2 bg-[#2a2a2a] border border-[#3a3a3a] focus:border-white focus:outline-none rounded-md text-sm"
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Every 6 Months">Every 6 Months</option>
                    <option value="Yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Preparation Time (days)</label>
                  <input
                    type="text"
                    name="preparationTime"
                    value={formData.preparationTime}
                    onChange={handleChange}
                    onFocus={(e) => {
                      if (e.target.value === '0') {
                        e.target.select();
                      }
                    }}
                    className="w-[300px] p-2 bg-[#2a2a2a] border border-[#3a3a3a] focus:border-white focus:outline-none rounded-md text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Grace Period (days)</label>
                  <input
                    type="text"
                    name="gracePeriod"
                    value={formData.gracePeriod}
                    onChange={handleChange}
                    onFocus={(e) => {
                      if (e.target.value === '0') {
                        e.target.select();
                      }
                    }}
                    className="w-[300px] p-2 bg-[#2a2a2a] border border-[#3a3a3a] focus:border-white focus:outline-none rounded-md text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="pt-2 text-left">
            <label className="block text-sm font-medium mb-2 text-gray-300">Next Due Date:</label>
            <div className="w-[300px]">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledMobileDatePicker
                  value={formData.nextDueDate}
                  onChange={handleDateChange}
                  slotProps={{
                    ...dateTimePickerSlotProps,
                    textField: {
                      ...getCommonPickerStyles('300px'),
                    }
                  }}
                  shouldDisableDate={shouldDisableDate}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          {taskId === 'new' && (
            <button
              type="submit"
              className="px-4 py-2 base-button rounded-md text-white"
            >
              Create Task
            </button>
          )}
        </div>
      </form>

      {taskId && taskId !== 'new' && activeRecurringTask && (
        <TaskHistory
          task={activeRecurringTask}
          onMarkDone={(date) => handleCompleteTask(taskId, date)}
          onLastCompletedChange={handleLastCompletedChange}
        />
      )}
    </div>
  );
}

export default RecurringTasks; 